import * as THREE from 'three';
import { type ShotStats } from '@/objects/golfBall';
import { UnitConversions } from '@/utils/units';
import { colors } from '@/utils/colors';

export type ShotStatsPanelOptions = {
  units?: 'imperial' | 'metric';
  /** Plane width in meters (height derives from the canvas aspect). */
  widthMeters?: number;
};

type Field = { label: string; value: (stats: ShotStats, shot?: OpenGolfSim.Shot) => string };

const CANVAS_W = 640;
const CANVAS_H = 400;

/**
 * World-space shot stats — the MR stand-in for the DOM UIShotData panel,
 * built on the AimPoint canvas-texture pattern. Placed once beside the
 * anchored tee like a real range monitor; yaw-billboards toward the head.
 */
export class ShotStatsPanel {
  object: THREE.Group;
  units: 'imperial' | 'metric';

  #canvas: HTMLCanvasElement;
  #ctx: CanvasRenderingContext2D;
  #texture: THREE.CanvasTexture;
  #fields: Field[];
  #cacheKey = '';
  #presetLabel = '';
  #statusLabel = '';

  constructor(options: ShotStatsPanelOptions = {}) {
    this.units = options.units ?? 'imperial';

    this.#fields = [
      {
        label: 'BALL SPEED',
        value: (_s, shot) => shot
          ? `${Math.round(this.units === 'imperial' ? shot.ballSpeed : UnitConversions.milesPerHourToMetersPerSecond(shot.ballSpeed))} ${this.units === 'imperial' ? 'MPH' : 'M/S'}`
          : '—',
      },
      { label: 'CARRY', value: (s) => this.#distance(s.carry) },
      { label: 'TOTAL', value: (s) => this.#distance(s.total) },
      {
        label: 'APEX',
        value: (s) => `${Math.round(this.units === 'imperial' ? UnitConversions.metersToFeet(s.apex) : s.apex)} ${this.units === 'imperial' ? 'FT' : 'M'}`,
      },
      { label: 'SPIN', value: (_s, shot) => shot ? `${Math.round(shot.spinSpeed)} RPM` : '—' },
    ];

    this.#canvas = document.createElement('canvas');
    this.#canvas.width = CANVAS_W;
    this.#canvas.height = CANVAS_H;
    const ctx = this.#canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to get 2d context for stats panel');
    this.#ctx = ctx;

    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;

    const width = options.widthMeters ?? 0.55;
    const height = width * (CANVAS_H / CANVAS_W);
    const material = new THREE.MeshBasicMaterial({
      map: this.#texture,
      transparent: true,
      alphaTest: 0.1,
      side: THREE.DoubleSide,
      fog: false,
    });
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
    panel.userData.mrMaskExempt = true;

    this.object = new THREE.Group();
    this.object.name = 'ShotStatsPanel';
    this.object.userData.mrMaskExempt = true;
    this.object.add(panel);
    this.object.visible = false;

    this.#draw({} as ShotStats);
  }

  /**
   * Beside the mat, off the target line, across the ball from the player:
   * right of the line for a right-handed golfer (side=1), left for a
   * left-handed one (side=-1) — never in the stance or swing plane.
   */
  placeAtTee(teePosition: THREE.Vector3, yaw: number, side: 1 | -1 = 1) {
    // 0.98, not 1.15: the course map hovers on the same side directly above
    // this, and at the old height the two crowded each other. Slightly lower
    // also reads better on its own — it sits nearer the ball.
    const offset = new THREE.Vector3(-0.9 * side, 0.98, 0.6).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    this.object.position.copy(teePosition).add(offset);
    this.object.visible = true;
  }

  /** Yaw-only billboard — text stays upright. */
  face(headPosition: THREE.Vector3) {
    if (!this.object.visible) return;
    const dx = headPosition.x - this.object.position.x;
    const dz = headPosition.z - this.object.position.z;
    this.object.rotation.y = Math.atan2(dx, dz);
  }

  setPresetLabel(label: string) {
    if (this.#presetLabel === label) return;
    this.#presetLabel = label;
    this.#cacheKey = '';
  }

  setStatusLabel(label: string) {
    if (this.#statusLabel === label) return;
    this.#statusLabel = label;
    this.#cacheKey = '';
  }

  /** Live during flight — redraws only when a rounded value changes. */
  update(stats: ShotStats, shot?: OpenGolfSim.Shot) {
    const key = this.#fields.map(f => f.value(stats, shot)).join('|')
      + `|${this.#presetLabel}|${this.#statusLabel}`;
    if (key === this.#cacheKey) return;
    this.#cacheKey = key;
    this.#draw(stats, shot);
  }

  #distance(meters: number) {
    if (this.units === 'imperial') {
      return meters < 5
        ? `${UnitConversions.metersToFeet(meters).toFixed(1)} FT`
        : `${Math.round(UnitConversions.metersToYards(meters))} YD`;
    }
    return `${Math.round(meters)} M`;
  }

  #draw(stats: ShotStats, shot?: OpenGolfSim.Shot) {
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    ctx.fillStyle = colors.background ?? 'rgba(17,28,28,0.9)';
    ctx.beginPath();
    ctx.roundRect(0, 0, CANVAS_W, CANVAS_H, 28);
    ctx.fill();

    const rowH = 64;
    const top = 36;
    ctx.textBaseline = 'middle';
    for (let i = 0; i < this.#fields.length; i++) {
      const y = top + i * rowH;
      const field = this.#fields[i];
      ctx.font = 'bold 28px Rubik, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.textAlign = 'left';
      ctx.fillText(field.label, 36, y);
      ctx.font = 'bold 36px Rubik, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'right';
      ctx.fillText(field.value(stats, shot), CANVAS_W - 36, y);
    }

    ctx.font = 'bold 24px Rubik, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillStyle = colors.primary ?? '#21d48d';
    const footer = [this.#presetLabel, this.#statusLabel].filter(Boolean).join('   ·   ');
    ctx.fillText(footer, 36, CANVAS_H - 32);

    this.#texture.needsUpdate = true;
  }
}
